import axios, {
  HttpStatusCode,
  type AxiosError,
  type InternalAxiosRequestConfig
} from "axios";
import * as Application from "expo-application";
import { Platform } from "react-native";

import { WAYUP_API_URL } from "@repo/core/constants/global";
import {
  buildWayupApiClientHeaders,
  type WayupApiClientPlatform
} from "@repo/core/services/instances/wayup-api.headers";
import useWayupApiAuthStore, {
  type WayupApiAuthResponse
} from "@repo/core/stores/wayupApiAuth";
import { logAxiosRequestError } from "@repo/core/services/observability/requestErrorLogger";
import {
  expireAuthenticatedSession,
  rejectStaleSessionResponse,
  startAuthenticatedRequest
} from "@repo/core/services/session/sessionExpiration";
import {
  getSessionEpoch,
  isCurrentSessionEpoch,
  isSessionInvalidated,
  SessionExpiredFlowError
} from "@repo/core/services/session/sessionEpoch";
import { version as nativeAppVersionFallback } from "../../../../apps/wayup-expo/package.json";
import { version as webAppVersion } from "../../../../apps/wayup-web/package.json";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    _wayupAuthRetry?: boolean;
  }
}

const wayupApiPlatform = Platform.OS as WayupApiClientPlatform;
const wayupApiAppVersion =
  wayupApiPlatform === "web"
    ? webAppVersion
    : Application.nativeApplicationVersion || nativeAppVersionFallback;
const wayupApiUserAgent =
  wayupApiPlatform === "web" && typeof navigator !== "undefined"
    ? navigator.userAgent
    : undefined;

const wayupApiClientHeaders = buildWayupApiClientHeaders({
  appVersion: wayupApiAppVersion,
  platform: wayupApiPlatform,
  userAgent: wayupApiUserAgent
});

function attachWayupApiClientHeaders(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  Object.entries(wayupApiClientHeaders).forEach(([name, value]) => {
    config.headers.set(name, value);
  });

  if (__DEV__) {
    const method = config.method?.toUpperCase() || "UNKNOWN";
    const url = config.url?.replace(/[?#].*$/, "") || "unknown-url";

    console.info(
      `[WAYUP_API][REQUEST][${method}] ${url} | app_version=${wayupApiAppVersion} platform=${wayupApiPlatform}`
    );
  }

  return config;
}

const plainClient = axios.create({
  baseURL: WAYUP_API_URL,
  headers: { "Content-Type": "application/json" }
});

plainClient.interceptors.request.use(attachWayupApiClientHeaders);

plainClient.interceptors.response.use(undefined, (error) => {
  logAxiosRequestError("wayup_api", error);
  return Promise.reject(error);
});

let refreshInFlight: Promise<void> | null = null;

function handleWayupApiSessionExpired(error: unknown): void {
  expireAuthenticatedSession({ apiClient: "wayup_api", error });
}

async function refreshWayupAccessToken(): Promise<void> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const requestEpoch = getSessionEpoch();
    const refreshToken = useWayupApiAuthStore.getState().refreshToken;
    if (!refreshToken) {
      throw new Error("Wayup API: missing refresh token");
    }

    const { data } = await plainClient.post<WayupApiAuthResponse>(
      "/auth/refresh",
      { refresh_token: refreshToken }
    );

    if (isSessionInvalidated() || !isCurrentSessionEpoch(requestEpoch)) {
      throw new SessionExpiredFlowError();
    }

    useWayupApiAuthStore.getState().setFromAuthResponse(data);
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/**
 * Obtains tokens from Wayup API (separate from main app auth) and persists them.
 */
export async function loginWayupApi(credentials: {
  usuario: string;
  senha: string;
}): Promise<WayupApiAuthResponse> {
  const requestEpoch = getSessionEpoch();
  const { data } = await plainClient.post<WayupApiAuthResponse>(
    "/auth/token",
    credentials
  );
  if (isSessionInvalidated() || !isCurrentSessionEpoch(requestEpoch)) {
    throw new SessionExpiredFlowError();
  }
  useWayupApiAuthStore.getState().setFromAuthResponse(data);
  return data;
}

const wayupApiInstance = axios.create({
  baseURL: WAYUP_API_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

function isAuthEndpoint(config: InternalAxiosRequestConfig): boolean {
  const url = config.url || "";
  return url.includes("/auth/token") || url.includes("/auth/refresh");
}

wayupApiInstance.interceptors.request.use((config) => {
  attachWayupApiClientHeaders(config);
  startAuthenticatedRequest(config);

  const { accessToken } = useWayupApiAuthStore.getState();

  if (accessToken && !isAuthEndpoint(config)) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

wayupApiInstance.interceptors.response.use(
  (response) => {
    rejectStaleSessionResponse(response.config);
    return response;
  },
  async (err) => {
    if (err instanceof SessionExpiredFlowError) {
      return Promise.reject(err);
    }

    const config = err?.config as InternalAxiosRequestConfig | undefined;
    const { method, url, baseURL } = config || {};
    const status = err?.response?.status;

    try {
      rejectStaleSessionResponse(config);
    } catch (staleSessionError) {
      return Promise.reject(staleSessionError);
    }

    if (__DEV__ && process.env.LOG_REQUESTS === "true") {
      console.error(
        `[WAYUP_API][ERROR][${method?.toUpperCase()}] [${status}] ${JSON.stringify(
          {
            baseURL,
            url,
            data: err?.response?.data,
            request: {
              data: err?.config?.data,
              params: err?.config?.params
            },
            response: err?.response?.data
          }
        )}`
      );
    }

    const shouldTryRefresh =
      status === HttpStatusCode.Unauthorized &&
      config &&
      !config._wayupAuthRetry &&
      !isAuthEndpoint(config);

    if (!shouldTryRefresh) {
      logAxiosRequestError("wayup_api", err);
      return Promise.reject(err);
    }

    const refreshToken = useWayupApiAuthStore.getState().refreshToken;

    if (!refreshToken) {
      handleWayupApiSessionExpired(err);
      logAxiosRequestError("wayup_api", err);
      return Promise.reject(err);
    }

    try {
      await refreshWayupAccessToken();
      config._wayupAuthRetry = true;
      // Request interceptor runs again and attaches the new access token from the store
      return await wayupApiInstance.request(config);
    } catch (refreshOrRetryError) {
      if (refreshOrRetryError instanceof SessionExpiredFlowError) {
        return Promise.reject(refreshOrRetryError);
      }

      handleWayupApiSessionExpired(refreshOrRetryError);

      const failedConfig = (refreshOrRetryError as AxiosError).config;
      if (failedConfig?.url?.includes("/auth/refresh")) {
        logAxiosRequestError("wayup_api", err);
      }

      return Promise.reject(err);
    }
  }
);

export { wayupApiInstance };
